import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessions, logoutSpecificSession } from "../../redux/ApiSlice/AccountSettingSlice";
import SkeletonSessionHistory from "../../components/skeleton/SkeletonSessionHistory";
import { logoutAllSessions } from "../../redux/ApiSlice/LogoutSlice";
import { clearAccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";
import { setClearHomepageVideos } from "../../redux/ApiSlice/HomePageVideoSlice";
import { persistor } from "../../redux/store";
import { clearStorageKeepingPreferences } from "../../utils/userPreferences";

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

const AccountSessionHistory = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { sessions, sessionsLoading, sessionsError } = useSelector((state) => state.AccountSetting);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [isLogoutAll, setIsLogoutAll] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const sessionList = sessions ?? [];

    useEffect(() => {
        dispatch(fetchSessions());
    }, [dispatch]);

    useEffect(() => {
        document.body.style.overflow = showConfirmModal ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [showConfirmModal]);

    const handleSignOutSession = (sessionId) => {
        setSelectedSessionId(sessionId);
        setIsLogoutAll(false);
        setShowConfirmModal(true);
    };

    const handleSignOutAll = () => {
        setIsLogoutAll(true);
        setShowConfirmModal(true);
    };

    const confirmSignOut = async () => {
        if (!selectedSessionId) return;
        setIsSubmitting(true);
        try {
            await dispatch(logoutSpecificSession(selectedSessionId)).unwrap();
            setShowConfirmModal(false);
            setSelectedSessionId(null);
        } catch (error) {
            console.error("Error signing out session:", error);
            setShowConfirmModal(false);
            setSelectedSessionId(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmSignOutAll = async () => {
        setIsSubmitting(true);
        try {
            await dispatch(logoutAllSessions()).unwrap();

            await persistor.flush();
            await persistor.purge();
            dispatch(clearAccountProfile());
            dispatch(setClearHomepageVideos());

            const keepLogin = localStorage.getItem("keepMeLogin");
            localStorage.removeItem('accessToken');
            if (keepLogin) {
                localStorage.removeItem("security_login");
                localStorage.removeItem("persist:root");
            } else {
                clearStorageKeepingPreferences();
            }

            setShowConfirmModal(false);
            setSelectedSessionId(null);
            setIsLogoutAll(false);

            setTimeout(() => {
                navigate("/login");
            }, 500);
        } catch (error) {
            console.error("Error signing out all sessions:", error);
            setShowConfirmModal(false);
            setSelectedSessionId(null);
            setIsLogoutAll(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const cancelSignOut = () => {
        setShowConfirmModal(false);
        setSelectedSessionId(null);
        setIsLogoutAll(false);
    };

    return (
        <>
            <div className="min-h-screen overflow-auto bg-gray-100 dark:bg-[#1E1D21]">
                <div className="bg-white dark:bg-[#1E1D21] relative min-h-screen">
                    <div className="sticky top-0 z-50">
                        <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                            <IoMdArrowBack
                                onClick={() => navigate("/settings/account")}
                                className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                            />
                            <p className="text-center font-bold text-[16px] text-black flex-grow">Session History</p>
                        </div>
                    </div>
                    <div className="px-3 pt-5">
                        <p className="text-[14px] text-black dark:text-white font-bold">Session History</p>
                        <p className="text-[12px] text-black dark:text-white pt-1">
                            Review recent login activity on your account. If you see any sessions you
                            don't recognize, revoke access or sign out immediately and update your password.
                        </p>
                    </div>
                    <div className="px-3 pt-3 flex justify-end">
                        <button
                            onClick={handleSignOutAll}
                            className="rounded-full bg-[#BDE6FF] hover:bg-[#9dd4f0] text-[12px] font-bold px-6 h-8 text-black transition-colors"
                        >
                            Logout All Sessions
                        </button>
                    </div>

                    <div className="px-3">
                        {sessionsLoading ? (
                            <SkeletonSessionHistory />
                        ) : sessionsError ? (
                            <div className="px-3 mt-5 text-center text-[12px] text-red-600">
                                Error loading sessions
                            </div>
                        ) : sessionList.length === 0 ? (
                            <div className="px-3 mt-5 text-center text-[12px] text-gray-600 dark:text-gray-400">
                                No active sessions found
                            </div>
                        ) : (
                            <div className="mt-5 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg">
                                {sessionList.map((session) => (
                                    <div key={session.session_key} className="flex items-center justify-between p-3 border-b-2 border-[#BDE6FF] dark:border-gray-800/50 last:border-b-0">
                                        <div className="flex flex-col gap-y-0.5">
                                            <p className="text-[11px] font-bold text-black dark:text-white">
                                                {formatDate(session.created_at)}
                                                {session.is_current && " (Current)"}
                                            </p>
                                            <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400">
                                                {session.device_name} - {session.ip_address}
                                            </p>
                                            {!session.is_current && (
                                                <p onClick={() => handleSignOutSession(session.session_key)} className="text-[11px] font-bold text-gray-600 dark:text-gray-400 cursor-pointer">
                                                    Sign Out
                                                </p>
                                            )}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {showConfirmModal && (
                        <div
                            className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
                            onClick={(e) => e.target === e.currentTarget && cancelSignOut()}
                        >
                            <div className="bg-white dark:bg-[#333333] p-4 rounded-lg shadow-lg w-[85%] max-w-sm mx-4">
                                <h2 className="text-[18px] font-bold text-black dark:text-white mb-2">
                                    {isLogoutAll ? "Sign Out All Sessions" : "Sign Out Session"}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400 mb-4 text-[12px] md:text-[14px]">
                                    {isLogoutAll
                                        ? "Are you sure you want to sign out of all sessions? You will be logged out from all devices and redirected to the login page."
                                        : "Are you sure you want to sign out of this session? This will end the session on that device."}
                                </p>
                                <div className="flex justify-center gap-3">
                                    <button
                                        disabled={isSubmitting}
                                        className="rounded-full bg-[#151515] hover:bg-[#272727] text-[12px] font-bold px-8 h-8 text-white flex-1 disabled:opacity-60"
                                        onClick={cancelSignOut}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        disabled={isSubmitting}
                                        className="rounded-full bg-[#BDE6FF] hover:bg-[#9dd4f0] text-[12px] font-bold px-8 h-8 text-black flex-1 disabled:opacity-60"
                                        onClick={isLogoutAll ? confirmSignOutAll : confirmSignOut}
                                    >
                                        {isSubmitting ? "..." : "Confirm"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AccountSessionHistory;